import { Z_INDEX } from '@va/constants';
import { CloseIconWithCircle, PencilEditIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { useConfirmationDialogContext } from '@va/shared/util-confirmation';
import { Button, HorizontalSeparator, MenuList, Paragraph } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { useCallback, useState } from 'react';
import { useDeleteConversation } from '../../../api';
import { AiChatbotView } from '../../../types';
import { useAiChatbotContext } from '../../aiChatbotContext';
import { DeleteConversationConfirmationModal } from '../DeleteConversationConfirmationModal';
import { useChatDetailsContext } from './ChatDetailsContext';

export const ChatOptionsButton = () => {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const translate = useTranslate();

  return (
    <TooltipWrapper
      content={
        <OptionsDropdown
          onClose={() => {
            setIsDropdownVisible(false);
          }}
        />
      }
      useDefaultStyle={false}
      trigger={'click'}
      open={isDropdownVisible}
      zIndex={Z_INDEX.modals - 1}
      outsidePress={() => {
        return true;
      }}
      tooltipClassNames={'shadow-outline rounded-t-9px rounded-b-15'}
    >
      <Paragraph
        className='text-interface-regular leading-interface-regular cursor-pointer whitespace-nowrap'
        colorClassName='text-primary'
        onClick={() => {
          setIsDropdownVisible((p) => !p);
        }}
      >
        {translate('aiChatbot.chat.options.button')}
      </Paragraph>
    </TooltipWrapper>
  );
};

const OptionsDropdown = ({ onClose }: { onClose: () => void }) => {
  const translate = useTranslate();
  const { conversation, mutateConversation } = useChatDetailsContext();
  const { openEditChatNameModal, setSelectedView } = useAiChatbotContext();
  const { confirm } = useConfirmationDialogContext();
  const { execute: deleteConversation } = useDeleteConversation();

  const handleDeleteClick = useCallback(() => {
    if (!conversation) return;

    confirm(
      async () => {
        await deleteConversation(conversation.id);
        setSelectedView(AiChatbotView.ChatsHistory);
      },
      ({ onClose, onConfirm }) => (
        <DeleteConversationConfirmationModal chatName={conversation.title} onClose={onClose} onConfirm={onConfirm} />
      ),
    );
  }, [conversation, confirm, deleteConversation, setSelectedView]);

  return (
    <div className={'bg-white'}>
      <div className={'pl-6u flex justify-between items-center'}>
        <span className={'text-interface-regular leading-interface-regular text-neutralBase-85'}>
          {translate('aiChatbot.chat.options.dropdownTitle')}
        </span>
        <Button
          size='small'
          color='quinary'
          icon={(_, color) => <CloseIconWithCircle className='w-10u h-10u' color={color} />}
          onClick={() => {
            onClose();
          }}
        />
      </div>
      <HorizontalSeparator className={'-mx-4u'} colorClassName={'bg-neutralBase-85'} />
      <div className={'bg-white p-4u'}>
        <MenuList
          className={'my-1u'}
          items={[
            {
              id: 'rename',
              label: translate('aiChatbot.chat.options.rename'),
              icon: <PencilEditIcon className={'w-15px h-15px'} />,
              endAdornment: <></>,
              className: 'text-neutralBase-93',
              labelClassName: '!text-interface-regular !leading-interface-regular',
              onClick: () => {
                if (!conversation) return;
                openEditChatNameModal(conversation, mutateConversation);
              },
            },
            {
              id: 'delete',
              label: translate('aiChatbot.chat.options.delete'),
              icon: <CloseIconWithCircle className={'w-15px h-15px'} />,
              endAdornment: <></>,
              className: 'text-neutralBase-93',
              labelClassName: '!text-interface-regular !leading-interface-regular',
              onClick: handleDeleteClick,
            },
          ]}
        />
        <Button
          size={'small'}
          color={'tertiary'}
          text={translate('button.cancel')}
          className={'w-full'}
          textClasses='text-interface-regular leading-interface-regular'
          onClick={() => {
            onClose();
          }}
        />
      </div>
    </div>
  );
};
